/** Small hooks shared by components and features. */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useSearchParams, type SetURLSearchParams } from "react-router";

/**
 * Loading-indicator timing from the interface guidelines: stay hidden for the first `delayMs`
 * (fast responses never flash a skeleton), then stay visible for at least `minVisibleMs`.
 */
export function useDelayedVisibility(active: boolean, { delayMs = 150, minVisibleMs = 300 } = {}): boolean {
  const [visible, setVisible] = useState(false);
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      const timer = window.setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
      }, delayMs);
      return () => window.clearTimeout(timer);
    }
    if (shownAt.current === null) return undefined;
    const remaining = Math.max(0, minVisibleMs - (Date.now() - shownAt.current));
    const timer = window.setTimeout(() => {
      shownAt.current = null;
      setVisible(false);
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [active, delayMs, minVisibleMs]);

  return visible;
}

/** The current time in ms, refreshed every `refreshMs` (render stays pure: no Date.now() in render). */
export function useNow(refreshMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), refreshMs);
    return () => window.clearInterval(timer);
  }, [refreshMs]);
  return now;
}

/** `value`, updated only after it has stopped changing for `delayMs`. */
export function useDebouncedValue<T>(value: T, delayMs = 200): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Live result of a CSS media query, e.g. useMediaQuery("(width < 64rem)"). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** True on macOS and iOS, where shortcuts use ⌘ instead of Ctrl. */
export function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform;
  return /mac|iphone|ipad|ipod/i.test(platform);
}

// Persisted in browsers: a new key would reset everyone's saved choice.
const SINGLE_KEY_SHORTCUTS_KEY = "perfume.shortcuts.singleKey";

/** Whether single-key shortcuts (no modifier) are on; Settings can turn them off (WCAG 2.1.4). */
export function singleKeyShortcutsEnabled(): boolean {
  try {
    return window.localStorage.getItem(SINGLE_KEY_SHORTCUTS_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSingleKeyShortcutsEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(SINGLE_KEY_SHORTCUTS_KEY, enabled ? "on" : "off");
  } catch {
    // Storage can be unavailable (private mode); the default stays on.
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export interface HotkeyOptions {
  /** Require ⌘ on Apple platforms and Ctrl elsewhere. */
  mod?: boolean;
  shift?: boolean;
  /** Fire even while focus is in a text field (only sensible with `mod`). */
  allowInFields?: boolean;
  enabled?: boolean;
}

/**
 * Global keyboard shortcut. `key` is compared case-insensitively with KeyboardEvent.key.
 * Shortcuts without a modifier never fire while typing and respect the single-key setting.
 */
export function useHotkey(key: string, handler: (event: KeyboardEvent) => void, options: HotkeyOptions = {}): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  const { mod = false, shift = false, allowInFields = false, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return undefined;
    const apple = isApplePlatform();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== key.toLowerCase()) return;
      const modPressed = apple ? event.metaKey : event.ctrlKey;
      if (mod !== modPressed || shift !== event.shiftKey || event.altKey) return;
      if (!mod && (!singleKeyShortcutsEnabled() || isTypingTarget(event.target))) return;
      if (!allowInFields && isTypingTarget(event.target)) return;
      event.preventDefault();
      handlerRef.current(event);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [key, mod, shift, allowInFields, enabled]);
}

/** Sets document.title as "<title> – Aldehyde" while mounted. */
export function useDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    document.title = title ? `${title} – Aldehyde` : "Aldehyde";
  }, [title]);
}

/**
 * `useSearchParams` for what a page keeps in its URL (filters, tabs, chart choices): a change replaces
 * the history entry and keeps the scroll position. A call that should start at the top passes
 * `{ preventScrollReset: false }`, as a new page of results does.
 */
export function useSearchParamsInPlace(): [URLSearchParams, SetURLSearchParams] {
  const [searchParams, setSearchParams] = useSearchParams();
  const setInPlace = useCallback<SetURLSearchParams>(
    (next, options) => setSearchParams(next, { replace: true, preventScrollReset: true, ...options }),
    [setSearchParams],
  );
  return [searchParams, setInPlace];
}
